import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { FadeUp, Overline, Stagger, StaggerItem, MaskedLine } from "@/components/site/Reveal";
import { EditorialMarquee } from "@/components/site/Marquee";
import { IMAGES, MANIFESTO } from "@/data/content";

const FounderProfile = ({ testid, name, role, photo, bio, flip }) => (
  <FadeUp>
    <div className="grid items-center gap-10 lg:grid-cols-12 lg:gap-16" data-testid={testid}>
      <div className={`lg:col-span-5 ${flip ? "lg:order-2" : ""}`}>
        <img
          src={photo}
          alt={`${name}, ${role} of Fore The Hell Of It`}
          className="w-full object-cover"
          data-testid={`${testid}-photo`}
        />
      </div>
      <div className={`lg:col-span-7 ${flip ? "lg:order-1" : ""}`}>
        <h3 className="font-display text-3xl font-extrabold uppercase tracking-tight md:text-4xl" data-testid={`${testid}-name`}>
          {name}
        </h3>
        <p className="mt-2 font-mono text-xs uppercase tracking-[0.25em] text-brand-accent" data-testid={`${testid}-role`}>{role}</p>
        <p className="mt-6 max-w-xl text-base leading-relaxed text-muted" data-testid={`${testid}-bio`}>{bio}</p>
      </div>
    </div>
  </FadeUp>
);

export default function About() {
  return (
    <main data-testid="about-page">
      <section className="mx-auto max-w-7xl px-5 pb-20 pt-36 md:px-8 md:pt-48 md:pb-28">
        <MaskedLine>
          <Overline>Our Story</Overline>
        </MaskedLine>
        <h1 className="mt-8 font-display text-5xl font-black uppercase leading-[0.9] tracking-tighter sm:text-7xl lg:text-8xl" data-testid="about-heading">
          <MaskedLine delay={0.15}>Built on</MaskedLine>
          <MaskedLine delay={0.3}>the <span className="text-brand-accent">course.</span></MaskedLine>
        </h1>
        <FadeUp delay={0.5} className="mt-10 grid gap-10 md:grid-cols-2">
          <p className="text-base leading-relaxed text-white/80 md:text-lg">
            Fore The Hell Of It was started by two best friends — Carlos Lopez and Joseph Buro —
            who share a love of golf, competition, and connecting with people from all walks of life.
          </p>
          <p className="text-base leading-relaxed text-muted md:text-lg">
            What began as a camera on a golf cart became a media brand: course vlogs, match play,
            travel, challenges, and long-form conversations with entrepreneurs, athletes, and leaders.
            Every episode brings it all together — talking the game, enjoying it together, and meeting
            the people who make it matter.
          </p>
        </FadeUp>
      </section>

      <EditorialMarquee />

      <section className="mx-auto max-w-7xl px-5 py-24 md:px-8 md:py-32" data-testid="about-founders">
        {/* Shared introduction */}
        <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16" data-testid="founders-intro">
          <FadeUp>
            <img
              src="/founders.jpg"
              alt="Carlos Lopez and Joseph Buro, co-founders of Fore The Hell Of It, on the course"
              className="w-full object-cover"
              data-testid="founders-photo"
            />
          </FadeUp>
          <FadeUp delay={0.1}>
            <Overline>The Founders</Overline>
            <h2 className="mt-4 font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-tighter sm:text-5xl lg:text-6xl" data-testid="about-founders-heading">
              Meet the<br /><span className="text-brand-accent">Founders.</span>
            </h2>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-muted md:text-lg">
              FORE the Hell of It was founded by Carlos Lopez and Joseph Buro, two longtime
              friends with a shared background in emergency services and a belief that some of
              the best conversations happen outside the traditional studio. FORE combines golf,
              personality, storytelling, and real relationships to create content built around
              the people on the course—not just the scorecard.
            </p>
          </FadeUp>
        </div>

        {/* Individual profiles */}
        <div className="mt-20 flex flex-col gap-20 md:mt-28 md:gap-28">
          <FounderProfile
            testid="founder-carlos"
            name="Carlos Lopez"
            role="Co-Founder & CEO"
            photo="/carlos-lopez.jpg"
            bio="Carlos brings a decade of experience in emergency services and a career built around leadership, sales, and relationship development. With experience spanning B2B and B2C sales, fundraising initiatives, contract management, and negotiation, he has built his career around bringing people together and turning opportunities into action. At FORE, Carlos leads the company's strategy, partnerships, growth, and long-term vision."
          />
          <FounderProfile
            testid="founder-joseph"
            name="Joseph Buro"
            role="Co-Founder & COO"
            photo="/joseph-buro.jpg"
            flip
            bio="Joe brings a decade of experience in emergency services, where teamwork, communication, and the ability to connect with people have been central throughout his career. His background also spans the culinary industry and public works, giving him experience across service, operations, and community-facing environments. Known for building genuine relationships and bringing a creative perspective to the table, Joe leads FORE's day-to-day operations while helping shape its content, culture, and the experiences that define the brand."
          />
        </div>
      </section>

      <section className="relative overflow-hidden border-t border-white/10" data-testid="about-image-band">
        <img src={IMAGES.hero} alt="Golf course from above" className="h-[50vh] w-full object-cover contrast-125 saturate-[0.8] md:h-[70vh]" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/40" />
        <div className="absolute bottom-8 left-5 md:bottom-12 md:left-8">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/70">On location — chasing the story</p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-28 md:px-8 md:py-40" data-testid="about-mission">
        <FadeUp className="max-w-3xl">
          <Overline>What Fore Is About</Overline>
          <h2 className="mt-6 font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-tighter sm:text-5xl lg:text-6xl" data-testid="about-mission-heading">
            18 Holes. Good People.<br /><span className="text-brand-accent">Great Stories.</span>
          </h2>
          <p className="mt-8 text-base leading-relaxed text-muted md:text-lg">
            Fore brings golf, conversation and personality together for a round that's about a lot more than the scorecard.
          </p>
        </FadeUp>
        <Stagger className="mt-20 grid gap-5 md:mt-24 md:grid-cols-2 md:gap-6">
          {MANIFESTO.map((m) => (
            <StaggerItem key={m.n}>
              <div
                className="group relative h-full bg-ink-2 p-8 transition-all duration-500 hover:-translate-y-1.5 hover:bg-ink-3 md:p-12"
                data-testid={`about-value-${m.n}`}
              >
                <span className="font-mono text-xs tracking-[0.35em] text-brand-accent/70">{m.n}</span>
                <h3 className="mt-6 font-display text-3xl font-extrabold uppercase tracking-tight text-white md:text-4xl">
                  {m.title}
                </h3>
                <p className="mt-4 max-w-md text-sm leading-relaxed text-muted md:text-base">{m.body}</p>
                <span className="mt-8 block h-px w-10 bg-brand-accent/40 transition-all duration-500 group-hover:w-20 group-hover:bg-brand-accent" />
              </div>
            </StaggerItem>
          ))}
        </Stagger>
        <FadeUp delay={0.2} className="mt-16">
          <Link
            to="/contact?type=guest"
            data-testid="about-guest-cta"
            className="group inline-flex items-center gap-3 bg-white px-7 py-4 font-mono text-xs font-medium uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-brand-accent"
          >
            Want to play a round with us? Become a Guest
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </FadeUp>
      </section>
    </main>
  );
}
